/**
 * 认证服务
 * 处理登录、登出、Token 刷新等认证相关业务逻辑
 */

import type { HttpClient } from '../http/HttpClient'
import type { IStorage } from '../storage/Storage'
import type {
  LoginParams,
  WechatLoginParams,
  MiniProgramLoginParams,
  QRCodeInfo,
  SendVerifyCodeResponse,
  SlideCaptchaInfo,
  CreateSsoCodeParams,
  SsoCodeResponse,
  UserInfo,
  LoginResponse,
  AuthSession
} from '../../types/auth'
import { BaseService } from './BaseService'
import { AuthStore } from '../stores/AuthStore'
import { AuthAPI } from '../api/auth'
import { Polling } from '../utils/polling'
import { isValidPhone } from '../utils/validator'
import { DeviceInfoManager } from './DeviceInfoManager'
import { DeviceStore } from '../stores/DeviceStore'
import { SourceStore } from '../stores/SourceStore'
import { getApplet, getWxLoginUrl } from '../config/SDKConfigManager'
import { CsdError } from '../../types/error'
import { ERROR_CODES } from '../../constants/errors'

export class AuthService extends BaseService {
  private authStore: AuthStore
  private authAPI: AuthAPI
  private qrLoginPolling: Polling | null = null
  private miniProgramQRLoginPolling: Polling | null = null
  private deviceInfoManager?: DeviceInfoManager
  private verifyCodeCountdown: number = 0
  private verifyCodeTimer: any = null
  private readonly miniProgramQrcodeCacheKey = 'mpWxLoginQrcode'
  private tokenRefreshPromise: Promise<void> | null = null
  private readonly enableTokenRefresh: boolean
  private readonly tabId: string
  private refreshBroadcastChannel: BroadcastChannel | null = null
  private readonly refreshLockKey = 'csd_auth_refresh_lock'
  private readonly refreshLockTtl = 12_000

  constructor(http: HttpClient, storage: IStorage, options: { enableTokenRefresh?: boolean } = {}) {
    super(http, storage)
    this.enableTokenRefresh = options.enableTokenRefresh === true
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`
    this.authStore = new AuthStore(storage)
    this.authAPI = new AuthAPI(http)
    this.initSessionBroadcast()
  }

  /**
   * 获取认证状态
   */
  getAuthState() {
    return this.authStore.getState()
  }

  /**
   * 获取 AuthStore 实例
   */
  getAuthStore() {
    return this.authStore
  }

  /**
   * 获取设备信息管理器（懒加载）
   */
  private getDeviceInfoManager(): DeviceInfoManager {
    if (!this.deviceInfoManager) {
      const deviceStore = new DeviceStore(this.storage)
      const sourceStore = new SourceStore(this.storage)
      this.deviceInfoManager = new DeviceInfoManager(deviceStore, sourceStore, this.storage)
    }
    return this.deviceInfoManager
  }

  private initSessionBroadcast(): void {
    if (typeof BroadcastChannel === 'undefined') return
    try {
      this.refreshBroadcastChannel = new BroadcastChannel('csd_auth_session')
      this.refreshBroadcastChannel.onmessage = (event) => {
        const payload = event?.data
        if (!payload || payload.ownerTabId === this.tabId) return
        if (payload.type === 'SESSION_UPDATED') {
          this.applyBroadcastSession(payload.session)
        } else if (payload.type === 'SESSION_CLEARED') {
          this.authStore.clearLoginInfo()
        }
      }
    } catch (error) {
      console.error('[AuthService] Failed to initialize session broadcast', error)
    }
  }

  private applyBroadcastSession(session: AuthSession | null): void {
    if (!session?.accessToken || !session.refreshToken || !session.sessionId) return
    const current = this.authStore.getAuthSession()
    if (current && current.sessionId !== session.sessionId) return
    if (current && current.refreshVersion > session.refreshVersion) return
    this.authStore.setAuthSession(session)
  }

  private broadcastSessionUpdated(session: AuthSession): void {
    try {
      this.refreshBroadcastChannel?.postMessage({
        type: 'SESSION_UPDATED',
        ownerTabId: this.tabId,
        session: this.toBroadcastSession(session)
      })
    } catch (error) {
      console.error('[AuthService] Failed to broadcast session update', error)
    }
  }

  private toBroadcastSession(session: AuthSession): AuthSession {
    let userInfo: UserInfo | null = null
    try {
      userInfo = session.userInfo ? JSON.parse(JSON.stringify(session.userInfo)) : null
    } catch {
      userInfo = null
    }

    return {
      ...session,
      // BroadcastChannel 使用结构化克隆，不能携带 Vue 响应式代理或函数。
      userInfo
    }
  }

  private broadcastSessionCleared(): void {
    try {
      this.refreshBroadcastChannel?.postMessage({
        type: 'SESSION_CLEARED',
        ownerTabId: this.tabId
      })
    } catch (error) {
      console.error('[AuthService] Failed to broadcast session clear', error)
    }
  }

  private toAuthSession(response: LoginResponse, fallbackUserInfo: UserInfo | null = null): AuthSession | null {
    const accessToken = response.accessToken || response.token
    if (
      !accessToken ||
      !response.refreshToken ||
      !response.accessTokenExpiresAt ||
      !response.refreshTokenExpiresAt ||
      !response.sessionId ||
      typeof response.refreshVersion !== 'number'
    ) {
      return null
    }

    return {
      accessToken,
      refreshToken: response.refreshToken,
      accessTokenExpiresAt: response.accessTokenExpiresAt,
      refreshTokenExpiresAt: response.refreshTokenExpiresAt,
      sessionId: response.sessionId,
      refreshVersion: response.refreshVersion,
      userInfo: response.userInfo || fallbackUserInfo || null
    }
  }

  /**
   * 保存登录响应。
   * 只有后端返回完整双 token 契约时才写入可刷新 session；旧单 token 不自动获得刷新能力。
   */
  private async setLoginResponse(response: LoginResponse, fallbackUserInfo: UserInfo | null = null): Promise<string> {
    const session = this.toAuthSession(response, fallbackUserInfo)
    if (session) {
      this.setAuthSession(session)
      return session.accessToken
    }

    this.authStore.setLoginInfo(response.token, response.userInfo || fallbackUserInfo || null)
    this.broadcastSessionCleared()
    return response.token
  }

  /**
   * 保存旧版登录 token。外部 URL 注入等场景没有 refresh token，不接入自动刷新。
   */
  private async setLoginInfo(token: string, userInfo: UserInfo | null = null): Promise<string> {
    this.authStore.setLoginInfo(token, userInfo)
    this.broadcastSessionCleared()
    return token
  }

  /**
   * 接收外部登录态传入的 token；标准刷新要求登录源头直接返回 refresh token。
   */
  async rotateLoginToken(token: string, userInfo: UserInfo | null = null): Promise<string> {
    return this.setLoginInfo(token, userInfo)
  }

  private setAuthSession(session: AuthSession): void {
    this.authStore.setAuthSession(session)
    this.broadcastSessionUpdated(session)
  }

  /**
   * 刷新 token 已临时关闭，SDK 初始化时不再自动刷新历史登录态。
   */
  refreshExistingTokenOnInit(): void {
    if (!this.enableTokenRefresh || !this.authStore.getAuthSession() || this.tokenRefreshPromise) return

    void this.refreshToken().catch((error) => {
      console.error('[AuthService] Initial token refresh failed:', error)
    })
  }

  /**
   * 刷新 token 已临时关闭，请求链路无需等待刷新任务。
   */
  async waitForTokenRefresh(): Promise<void> {
    if (!this.enableTokenRefresh || !this.tokenRefreshPromise) return
    try {
      await this.tokenRefreshPromise
    } catch (error) {
      console.error('[AuthService] Waiting token refresh failed:', error)
    }
  }

  /**
   * 开始二维码登录
   */
  async startQRLogin(onStatusChange?: (status: string) => void): Promise<QRCodeInfo> {
    try {
      // 获取二维码
      const qrCodeData = await this.authAPI.getLoginQRCode()

      // 计算过期时间（默认 5 分钟）
      // const expiresIn = qrCodeData.expiresIn || 300
      // 实际 1分钟过期
      const expiresIn = 60

      const qrCodeInfo: QRCodeInfo = {
        url: qrCodeData.url,
        content: qrCodeData.content,
        ewm_id: qrCodeData.ewm_id,
        expire_at: Date.now() + expiresIn * 1000
      }

      // 保存二维码信息
      this.authStore.qrCodeInfo = qrCodeInfo

      // 防止重复触发登录成功回调
      let hasConfirmed = false

      // 开始轮询登录状态
      this.qrLoginPolling = new Polling({
        interval: 1000, // 每 1 秒轮询一次（兼容旧版）
        timeout: expiresIn * 1000, // 二维码过期时间
        onPoll: (attempt) => {
          console.log(`[AuthService] Polling login status, attempt: ${attempt}`)
        },
        onError: (error) => {
          console.error('[AuthService] Polling error:', error)
        }
      })

      // 启动轮询
      this.qrLoginPolling
        .start(
          async () => {
            const status = await this.authAPI.checkLoginStatus(qrCodeInfo.ewm_id)
            return status
          },
          (status) => {
            // 如果登录成功（只要有 token 就算成功，userInfo 可能为空）
            if (status.status === 'confirmed' && status.token) {
              return true
            }

            // 如果二维码过期或取消，停止轮询
            if (status.status === 'expired' || status.status === 'cancelled') {
              if (onStatusChange) {
                onStatusChange(status.status)
              }
              return true
            }

            // 其他状态（pending）不触发回调，继续轮询
            return false
          }
        )
        .then(async (status) => {
          if (status.status === 'confirmed' && status.token && !hasConfirmed) {
            hasConfirmed = true
            await this.setLoginResponse(status as LoginResponse, status.userInfo || null)
            if (onStatusChange) {
              onStatusChange(status.status)
            }
          }
        })
        .catch((error) => {
          // 轮询超时时，触发过期回调
          if (error?.message === 'Polling timeout') {
            console.log('[AuthService] QR code polling timeout, triggering expired status')
            if (onStatusChange) {
              onStatusChange('expired')
            }
          } else {
            this.handleError(error, 'QR login polling failed')
          }
        })

      return qrCodeInfo
    } catch (error) {
      return this.handleError(error, 'Failed to start QR login')
    }
  }

  /**
   * 微信登录（跳转授权，兼容旧版）
   */
  async wechatLoginRedirect(callbackUrl: string): Promise<void> {
    const wxLoginUrl = getWxLoginUrl()
    if (!wxLoginUrl) {
      throw new Error('wxLoginUrl is not configured')
    }

    const deviceInfo = this.getDeviceInfoManager()
    const common = deviceInfo.getCommonParams()
    const promotion = deviceInfo.getPromotionParams()

    const params: Record<string, string> = {
      soft_id: common.soft_id,
      curr_ver: common.curr_ver,
      device_id: common.device_id,
      mac_code: common.mac_code,
      device_code: common.device_code,
      os: String(common.os),
      source: common.source,
      ekey: common.ekey,
      callback_page: encodeURIComponent(callbackUrl)
    }
    if (promotion.mp) params.mp = promotion.mp
    if (promotion.invite_id) params.invite_id = String(promotion.invite_id)

    const query = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')

    window.location.href = `${wxLoginUrl}?${query}`
  }

  /** 创建 SSO code */
  async createSsoCode(params: CreateSsoCodeParams): Promise<SsoCodeResponse> {
    try {
      return await this.authAPI.createSsoCode(params)
    } catch (error) {
      return this.handleError(error, 'Failed to create SSO code')
    }
  }

  /** 创建 SSO code 并跳转到后端返回的 redirect_url */
  async createSsoCodeAndRedirect(params: CreateSsoCodeParams): Promise<SsoCodeResponse> {
    try {
      if (typeof window === 'undefined' || !window.location?.assign) {
        throw new CsdError('SSO 跳转仅支持浏览器环境', ERROR_CODES.PARAMETER_ERROR)
      }

      const result = await this.authAPI.createSsoCode(params)
      window.location.assign(result.redirect_url)
      return result
    } catch (error) {
      return this.handleError(error, 'Failed to create SSO code and redirect')
    }
  }

  /**
   * 停止二维码登录
   */
  stopQRLogin(): void {
    if (this.qrLoginPolling) {
      this.qrLoginPolling.stop()
      this.qrLoginPolling = null
    }
    this.authStore.qrCodeInfo = null
  }

  async checkQRLoginStatus(
    ewmId: string,
    options: { gapTime?: number; maxTime?: number } = {}
  ): Promise<{ token: string }> {
    const gapTime = options.gapTime ?? 1000
    const maxTime = options.maxTime ?? 60

    this.stopQRLogin()
    this.qrLoginPolling = new Polling({
      interval: gapTime,
      maxAttempts: maxTime,
      stopOnError: false
    })

    const result = await this.qrLoginPolling.start(
      async () => this.authAPI.checkLoginStatus(ewmId),
      (status) => status.status === 'confirmed' && !!status.token
    )

    if (!result.token) {
      throw new Error('登录失败')
    }

    const token = await this.setLoginResponse(result as LoginResponse, result.userInfo || null)
    return { token }
  }

  /**
   * 发送验证码
   */
  async sendVerifyCode(phone: string): Promise<void> {
    try {
      // 验证手机号
      if (!isValidPhone(phone)) {
        throw new Error('手机号格式不正确')
      }

      // 检查是否在倒计时中
      if (this.verifyCodeCountdown > 0) {
        throw new Error(`请等待 ${this.verifyCodeCountdown} 秒后再试`)
      }

      // 发送验证码
      await this.authAPI.sendVerifyCode(phone)

      // 开始倒计时（60 秒）
      this.startVerifyCodeCountdown(60)
    } catch (error) {
      return this.handleError(error, 'Failed to send verify code')
    }
  }

  /**
   * 获取小程序登录二维码
   */
  async getMiniProgramLoginQrcode(options?: {
    env_version?: 'release' | 'trial' | 'devop'
    login_params?: string
  }): Promise<{ base64_img: string; uuid: string }> {
    const cached = this.storage.getItem<{ expireTime: number; base64_img: string; uuid: string }>(
      this.miniProgramQrcodeCacheKey
    )
    if (cached && cached.expireTime > Date.now()) {
      return { base64_img: cached.base64_img, uuid: cached.uuid }
    }

    const result = await this.authAPI.getMiniProgramLoginQrcode({
      env_version: options?.env_version || 'release',
      login_params: options?.login_params
    })
    const base64_img = result.base64_img.startsWith('data:image')
      ? result.base64_img
      : `data:image/jpeg;base64,${result.base64_img}`
    const payload = {
      expireTime: Date.now() + 240 * 1000,
      base64_img,
      uuid: result.uuid
    }
    this.storage.setItem(this.miniProgramQrcodeCacheKey, payload)
    return { base64_img, uuid: result.uuid }
  }

  /**
   * 小程序扫码登录状态轮询
   */
  async checkMiniProgramLoginStatus(
    login_uuid: string,
    options: { gapTime?: number; maxTime?: number } = {}
  ): Promise<{ token: string }> {
    const gapTime = options.gapTime ?? 1000
    const maxTime = options.maxTime ?? 60

    // 停止之前的轮询
    if (this.miniProgramQRLoginPolling) {
      this.miniProgramQRLoginPolling.stop()
    }

    this.miniProgramQRLoginPolling = new Polling({
      interval: gapTime,
      maxAttempts: maxTime,
      stopOnError: true
    })

    const result = await this.miniProgramQRLoginPolling.start(
      async () => this.authAPI.getMiniProgramLoginTokenByQRCode(login_uuid),
      (res) => !!res?.token
    )

    // 清理缓存二维码
    this.storage.removeItem(this.miniProgramQrcodeCacheKey)

    if (!result.token) {
      throw new Error('小程序登录失败')
    }

    const token = await this.setLoginResponse(result as LoginResponse, null)
    return { token }
  }

  /**
   * 停止小程序扫码登录轮询
   */
  stopMiniProgramQRLogin(): void {
    if (this.miniProgramQRLoginPolling) {
      this.miniProgramQRLoginPolling.stop()
      this.miniProgramQRLoginPolling = null
    }
  }

  /**
   * 获取小程序登录参数
   */
  async getMiniProgramLoginParamsByUUID(login_uuid: string): Promise<any> {
    return await this.authAPI.getMiniProgramLoginParamsByUUID(login_uuid)
  }

  /**
   * 绑定小程序登录 Token
   */
  async bindMiniProgramTokenByUUID(login_uuid: string): Promise<void> {
    await this.authAPI.bindMiniProgramTokenByUUID(login_uuid)
  }

  /**
   * 设备号自动登录
   * 使用当前设备信息自动登录，无需用户交互
   * 适用于：测试环境、内部工具、跳过扫码/手机号登录的场景
   */
  async deviceLogin(): Promise<void> {
    try {
      const deviceInfo = this.getDeviceInfoManager()
      const common = deviceInfo.getCommonParams()

      // 将公共参数转为 form 格式提交
      const params: Record<string, string> = {
        device_id:   common.device_id,
        mac_code:    common.mac_code,
        device_code: common.device_code,
        soft_id:     common.soft_id,
        curr_ver:    common.curr_ver,
        os:          String(common.os),
        source:      common.source,
      }

      const response = await this.authAPI.deviceLogin(params)
      // 写入 AuthStore，触发响应式 isLogin 更新；刷新 token 已临时关闭。
      await this.setLoginResponse(response)
    } catch (error) {
      return this.handleError(error, 'Device login failed')
    }
  }

  /**
   * 小程序登录（旧版接口）
   */
  async miniProgramLoginLegacy(params: { wxCode: string; login_uuid?: string }): Promise<any> {
    const deviceInfo = this.getDeviceInfoManager()
    const common = deviceInfo.getCommonParams()
    const applet = getApplet()

    const body: Record<string, unknown> = {
      ...common,
      code: params.wxCode,
      login_uuid: params.login_uuid
    }
    if (applet) {
      body.applet = applet
    }

    const result = await this.authAPI.miniProgramLoginLegacy(body)
    if (result?.token) {
      const token = await this.setLoginResponse(result as LoginResponse, result.userInfo || null)
      return { ...result, token }
    }
    return result
  }

  /**
   * 开始验证码倒计时
   */
  private startVerifyCodeCountdown(seconds: number): void {
    this.verifyCodeCountdown = seconds

    if (this.verifyCodeTimer) {
      clearInterval(this.verifyCodeTimer)
    }

    this.verifyCodeTimer = setInterval(() => {
      this.verifyCodeCountdown--

      if (this.verifyCodeCountdown <= 0) {
        clearInterval(this.verifyCodeTimer)
        this.verifyCodeTimer = null
      }
    }, 1000)
  }

  /**
   * 获取验证码倒计时
   */
  getVerifyCodeCountdown(): number {
    return this.verifyCodeCountdown
  }

  /**
   * 手机号登录
   */
  async phoneLogin(params: LoginParams): Promise<void> {
    try {
      // 验证手机号
      if (!isValidPhone(params.phone)) {
        throw new Error('手机号格式不正确')
      }

      // 验证验证码（支持 4 位或 6 位）
      if (!params.code || params.code.length < 4 || params.code.length > 6) {
        throw new Error('验证码格式不正确')
      }

      // 登录
      const response = await this.authAPI.phoneLogin(params)

      // 保存登录信息；刷新 token 已临时关闭。
      await this.setLoginResponse(response)
    } catch (error) {
      return this.handleError(error, 'Phone login failed')
    }
  }

  /**
   * 微信登录
   */
  async wechatLogin(params: WechatLoginParams): Promise<void> {
    try {
      const response = await this.authAPI.wechatLogin(params)
      await this.setLoginResponse(response)
    } catch (error) {
      return this.handleError(error, 'Wechat login failed')
    }
  }

  /**
   * 小程序登录
   */
  async miniProgramLogin(params: MiniProgramLoginParams): Promise<void> {
    try {
      const response = await this.authAPI.miniProgramLogin(params)
      await this.setLoginResponse(response)
    } catch (error) {
      return this.handleError(error, 'Mini program login failed')
    }
  }

  /**
   * 登出
   */
  async logout(): Promise<void> {
    // 停止二维码登录轮询
    this.stopQRLogin()
    this.stopMiniProgramQRLogin()

    // 清除验证码倒计时
    if (this.verifyCodeTimer) {
      clearInterval(this.verifyCodeTimer)
      this.verifyCodeTimer = null
    }
    this.verifyCodeCountdown = 0

    // 清除本地登录信息
    this.authStore.clearLoginInfo()
    this.broadcastSessionCleared()
  }

  /**
   * 刷新 Token。默认关闭，只有 enableTokenRefresh=true 时才会请求刷新接口。
   */
  async refreshToken(): Promise<void> {
    if (!this.enableTokenRefresh) return
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise
    }
    if (!this.authStore.getAuthSession()) return

    const refreshTask = this.doRefreshToken()
    this.tokenRefreshPromise = refreshTask
    try {
      await refreshTask
    } finally {
      if (this.tokenRefreshPromise === refreshTask) {
        this.tokenRefreshPromise = null
      }
    }
  }

  private async doRefreshToken(): Promise<void> {
    const snapshot = this.authStore.getAuthSession()
    if (!snapshot) return

    const releaseLock = this.acquireRefreshLock(snapshot)
    try {
      if (!releaseLock) {
        await this.waitForExternalRefresh(snapshot)
        return
      }

      const response = await this.authAPI.refreshSession({
        refreshToken: snapshot.refreshToken,
        sessionId: snapshot.sessionId,
        refreshVersion: snapshot.refreshVersion
      })
      const current = this.authStore.getAuthSession()
      if (!current || current.sessionId !== snapshot.sessionId) return
      if (current.refreshVersion !== snapshot.refreshVersion) return
      this.setAuthSession({
        ...response,
        userInfo: response.userInfo || current.userInfo || null
      })
    } catch (error) {
      return this.handleError(error, 'Token refresh failed')
    } finally {
      releaseLock?.()
    }
  }

  private getLocalStorage(): Storage | null {
    try {
      if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
      if (typeof localStorage !== 'undefined') return localStorage
    } catch {
      return null
    }
    return null
  }

  private acquireRefreshLock(session: AuthSession): (() => void) | null {
    const storage = this.getLocalStorage()
    if (!storage) {
      return () => undefined
    }

    const now = Date.now()
    const nextLock = {
      ownerTabId: this.tabId,
      sessionId: session.sessionId,
      refreshVersion: session.refreshVersion,
      expiresAt: now + this.refreshLockTtl
    }

    try {
      const rawLock = storage.getItem(this.refreshLockKey)
      if (rawLock) {
        const currentLock = JSON.parse(rawLock)
        const isSameSession =
          currentLock?.sessionId === session.sessionId &&
          currentLock?.refreshVersion === session.refreshVersion
        const isAlive = typeof currentLock?.expiresAt === 'number' && currentLock.expiresAt > now
        if (isSameSession && isAlive && currentLock.ownerTabId !== this.tabId) {
          return null
        }
      }

      storage.setItem(this.refreshLockKey, JSON.stringify(nextLock))
      const confirmed = JSON.parse(storage.getItem(this.refreshLockKey) || '{}')
      if (confirmed.ownerTabId !== this.tabId) {
        // localStorage 在隐私模式或测试环境可能不可写，降级为单窗口内存刷新。
        return () => undefined
      }

      return () => {
        try {
          const latest = JSON.parse(storage.getItem(this.refreshLockKey) || '{}')
          if (latest.ownerTabId === this.tabId) {
            storage.removeItem(this.refreshLockKey)
          }
        } catch {
          storage.removeItem(this.refreshLockKey)
        }
      }
    } catch (error) {
      console.error('[AuthService] Failed to acquire refresh lock', error)
      return () => undefined
    }
  }

  private async waitForExternalRefresh(snapshot: AuthSession): Promise<void> {
    const startedAt = Date.now()
    const timeout = this.refreshLockTtl + 1_000

    await new Promise<void>((resolve) => {
      const timer = setInterval(() => {
        const current = this.authStore.getAuthSession()
        const isUpdated =
          current?.sessionId === snapshot.sessionId &&
          current.refreshVersion > snapshot.refreshVersion
        const isChanged = current?.sessionId && current.sessionId !== snapshot.sessionId
        const isTimedOut = Date.now() - startedAt >= timeout
        if (isUpdated || isChanged || isTimedOut) {
          clearInterval(timer)
          resolve()
        }
      }, 100)
    })
  }

  /**
   * 检查登录状态
   */
  isLogin(): boolean {
    return this.authStore.isLogin.value
  }

  /**
   * 获取图片验证码
   */
  async getImageCaptcha() {
    try {
      return await this.authAPI.getImageCaptcha()
    } catch (error) {
      return this.handleError(error, '获取图片验证码失败')
    }
  }

  /**
   * 获取滑块验证码
   */
  async getSlideCaptcha(params: {
    phone?: string
    smsType?: 'login' | 'bind' | 'reset'
  } = {}): Promise<SlideCaptchaInfo> {
    try {
      if (params.phone && !isValidPhone(params.phone)) {
        throw new Error('Invalid phone number')
      }
      return await this.authAPI.getSlideCaptcha(params)
    } catch (error) {
      return this.handleError(error, '获取滑块验证码失败')
    }
  }

  /**
   * 发送验证码（带图片验证码）
   */
  async sendVerifyCodeWithCaptcha(params: {
    phone: string
    captcha_key: string
    captcha_code: string
    smsType: 'login' | 'bind' | 'reset'
  }): Promise<SendVerifyCodeResponse> {
    try {
      // 验证手机号
      if (!isValidPhone(params.phone)) {
        throw new Error('Invalid phone number')
      }
      if (!params.captcha_key) {
        throw new Error('图片验证码已失效，请刷新后重试')
      }
      if (!/^\d{4}$/.test(params.captcha_code)) {
        throw new Error('图片验证码格式不正确')
      }

      return await this.authAPI.sendVerifyCodeWithCaptcha(params)
    } catch (error) {
      return this.handleError(error, '发送验证码失败')
    }
  }

  /**
   * 发送验证码（带滑块验证）
   */
  async sendVerifyCodeWithSlide(params: {
    phone: string
    captcha_key: string
    slide_x: number
    slide_y: number
    smsType: 'login' | 'bind' | 'reset'
  }): Promise<SendVerifyCodeResponse> {
    try {
      if (!isValidPhone(params.phone)) {
        throw new Error('Invalid phone number')
      }
      if (!params.captcha_key) {
        throw new Error('滑块验证码已失效，请刷新后重试')
      }
      if (!Number.isFinite(params.slide_x) || !Number.isFinite(params.slide_y)) {
        throw new Error('滑块验证坐标无效')
      }

      const result = await this.authAPI.sendVerifyCodeWithSlide(params)
      if (result.code === 0 || result.code === 200) {
        this.startVerifyCodeCountdown(60)
      }
      return result
    } catch (error) {
      return this.handleError(error, '发送验证码失败')
    }
  }

  /**
   * 获取当前用户信息
   */
  getUserInfo() {
    return this.authStore.userInfo
  }

  /**
   * 获取当前 Token
   */
  getToken(): string | null {
    this.authStore.syncTokenFromStorage()
    return this.authStore.token
  }
}
